import type { Serializable, Serialized } from './serialization.types';

/**
 * Serializes the given data into a format suitable for server-side rendering (SSR).
 * This function handles both arrays and objects, ensuring that each element or property
 * is serialized using the `serializeUnit` function.
 *
 * @template T - The type of the data to be serialized. Must extend `Serializable`.
 * @param data - The data to serialize. Can be an object or an array of objects.
 * @returns The serialized data. If the input is an array, the return type will be an array
 *          of serialized elements. Otherwise, it will be a serialized object.
 */
export function serialize<T extends Serializable>(
  data: T
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): T extends any[] ? Serialized<T[number]> : Serialized<T> {
  if (Array.isArray(data))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return data.map(serializeUnit) as any;
  return Object.entries(data).reduce(
    (acc, [key, value]) => {
      const parsed = serializeUnit(value);
      if (parsed !== undefined) acc[key] = parsed;
      return acc;
    },
    {} as Record<string, unknown>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) as any;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function serializeUnit(data: any): any {
  if (data === undefined) return undefined;
  if (data instanceof Date) return data.toISOString();
  if (typeof data === 'function') return undefined; // Skip functions
  if (Array.isArray(data)) return data.map(serializeUnit).filter((item) => item !== undefined);
  if (typeof data === 'object' && data !== null) {
    const serialized: Record<string, unknown> = {};
    for (const key in data) {
      const value = serializeUnit(data[key]);
      if (value !== undefined) {
        serialized[key] = value;
      }
    }
    return serialized;
  }
  return data;
}
